/**
 * UI adapter: bridges React policy handlers to CreatePolicy / ApprovePolicy use cases (UC-11/12)
 * Enable via architectureFeatures.usePolicyMutationUseCases
 */
import { useCallback } from 'react';
import type { Policy } from '../../../../types/policy';
import {
  createPolicyUseCase,
  approvePolicyUseCase,
  submitPolicyForReviewUseCase,
  implementPolicyUseCase,
  activatePolicyUseCase,
  activatePolicyChallengeUseCase,
  triggerPolicyAuditUseCase,
} from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { policyManager } from '../../../../services/policyManager';
import { policyEnforcementEngine } from '../../../../services/policyEnforcementEngine';
import { PolicyStatus } from '../../../../types/policy';
import type {
  CreatePolicyInput,
  CreatePolicyOutput,
} from '../../../application/use-cases/CreatePolicyUseCase';
import type {
  ApprovePolicyInput,
  ApprovePolicyOutput,
} from '../../../application/use-cases/ApprovePolicyUseCase';
import type {
  SubmitPolicyForReviewInput,
  SubmitPolicyForReviewOutput,
} from '../../../application/use-cases/SubmitPolicyForReviewUseCase';
import type {
  ImplementPolicyInput,
  ImplementPolicyOutput,
} from '../../../application/use-cases/ImplementPolicyUseCase';
import type {
  ActivatePolicyInput,
  ActivatePolicyOutput,
} from '../../../application/use-cases/ActivatePolicyUseCase';
import type {
  ActivatePolicyChallengeInput,
  ActivatePolicyChallengeOutput,
} from '../../../application/use-cases/ActivatePolicyChallengeUseCase';
import type {
  TriggerPolicyAuditInput,
  TriggerPolicyAuditOutput,
} from '../../../application/use-cases/TriggerPolicyAuditUseCase';

export type {
  CreatePolicyInput,
  CreatePolicyOutput,
  ApprovePolicyInput,
  ApprovePolicyOutput,
  SubmitPolicyForReviewInput,
  SubmitPolicyForReviewOutput,
  ImplementPolicyInput,
  ImplementPolicyOutput,
  ActivatePolicyInput,
  ActivatePolicyOutput,
  ActivatePolicyChallengeInput,
  ActivatePolicyChallengeOutput,
  TriggerPolicyAuditInput,
  TriggerPolicyAuditOutput,
};

/** Run create through clean architecture use case (UC-11) */
export function executeCreatePolicy(input: CreatePolicyInput): CreatePolicyOutput {
  if (!architectureFeatures.usePolicyMutationUseCases) {
    throw new Error('usePolicyMutationUseCases feature flag is disabled');
  }
  return createPolicyUseCase.execute(input);
}

/** Run approve through clean architecture use case (UC-12) */
export function executeApprovePolicy(input: ApprovePolicyInput): ApprovePolicyOutput {
  if (!architectureFeatures.usePolicyMutationUseCases) {
    throw new Error('usePolicyMutationUseCases feature flag is disabled');
  }
  return approvePolicyUseCase.execute(input);
}

/** Legacy fallback — inline create via policyManager singleton */
export function executeLegacyCreatePolicy(input: CreatePolicyInput): CreatePolicyOutput {
  try {
    const policy = policyManager.createPolicy(input.templateId, input.customization);
    return { ok: true, policy };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Legacy fallback — inline approve via policyManager singleton */
export function executeLegacyApprovePolicy(input: ApprovePolicyInput): ApprovePolicyOutput {
  try {
    const policy = policyManager.approvePolicy(input.policyId, input.approver ?? 'Player');
    return { ok: true, policy };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Resolve create — UC-11 when flag on, else legacy path */
export function resolveCreatePolicy(input: CreatePolicyInput): CreatePolicyOutput {
  if (architectureFeatures.usePolicyMutationUseCases) {
    return executeCreatePolicy(input);
  }
  return executeLegacyCreatePolicy(input);
}

/** Resolve approve — UC-12 when flag on, else legacy path */
export function resolveApprovePolicy(input: ApprovePolicyInput): ApprovePolicyOutput {
  if (architectureFeatures.usePolicyMutationUseCases) {
    return executeApprovePolicy(input);
  }
  return executeLegacyApprovePolicy(input);
}

export function resolveSubmitPolicyForReview(
  input: SubmitPolicyForReviewInput,
): SubmitPolicyForReviewOutput {
  if (architectureFeatures.usePolicyMutationUseCases) {
    return submitPolicyForReviewUseCase.execute(input);
  }
  try {
    const policy = policyManager.updatePolicy(input.policyId, {
      status: PolicyStatus.UnderReview,
    });
    return { ok: true, policy };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export function resolveImplementPolicy(input: ImplementPolicyInput): ImplementPolicyOutput {
  if (architectureFeatures.usePolicyMutationUseCases) {
    return implementPolicyUseCase.execute(input);
  }
  try {
    const policy = policyManager.implementPolicy(input.policyId, input.implementer ?? 'Player');
    return { ok: true, policy };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export function resolveActivatePolicy(input: ActivatePolicyInput): ActivatePolicyOutput {
  if (architectureFeatures.usePolicyMutationUseCases) {
    return activatePolicyUseCase.execute(input);
  }
  try {
    const policy = policyManager.enforcePolicy(input.policyId);
    return { ok: true, policy };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export function resolveActivatePolicyChallenge(
  input: ActivatePolicyChallengeInput,
): ActivatePolicyChallengeOutput {
  if (architectureFeatures.usePolicyMutationUseCases) {
    return activatePolicyChallengeUseCase.execute(input);
  }
  try {
    policyManager.activateChallenge(input.challengeId);
    const challenges = policyManager.getChallenges();
    return {
      ok: true,
      challenges,
      activatedChallenge: challenges.find((c) => c.id === input.challengeId),
    };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export function resolveTriggerPolicyAudit(
  input: TriggerPolicyAuditInput,
): TriggerPolicyAuditOutput {
  if (architectureFeatures.usePolicyMutationUseCases) {
    return triggerPolicyAuditUseCase.execute(input);
  }
  try {
    const audit = policyEnforcementEngine.triggerComplianceAudit(input.policyId);
    return { ok: true, audit };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Hook helper for usePolicyHandlers integration.
 * Returns stable callbacks that run UC-11/12 when feature flag is on.
 */
export function usePolicyMutationRunner() {
  const runCreate = useCallback(
    (input: CreatePolicyInput): CreatePolicyOutput | null => {
      if (!architectureFeatures.usePolicyMutationUseCases) {
        return null;
      }
      return executeCreatePolicy(input);
    },
    [],
  );

  const runApprove = useCallback(
    (input: ApprovePolicyInput): ApprovePolicyOutput | null => {
      if (!architectureFeatures.usePolicyMutationUseCases) {
        return null;
      }
      return executeApprovePolicy(input);
    },
    [],
  );

  return { runCreate, runApprove };
}